import { visuallyHidden } from "@mui/utils";
import { REHAB_TRIGGERING_EVENT } from "core/consts";
import { PENSION_INSURANCE_NUMBER_CHARACTER_LIMIT } from "core/schemas/shared";
import ConnectedCheckboxGroup, {
  CheckboxGroupProps,
} from "ds_legacy/components/CheckboxGroup";
import CheckboxInputField from "ds_legacy/components/CheckboxInputField";
import { margin, sizing } from "ds_legacy/materials/metrics";
import {
  DatePickerWithLabel,
  LARGE_INPUT_WIDTH,
  PatientFormSubheading,
  SMALL_INPUT_MIN_WIDTH,
  Section,
  SectionRow,
  TextInputWithLabel,
} from "dsl/ecosystems/PatientForms/components";
import { useTranslations } from "translations";

export const IntroSection = () => {
  const translations = useTranslations();

  const options: CheckboxGroupProps["items"] = [
    {
      id: REHAB_TRIGGERING_EVENT.SURGERY,
      label: translations.patientForms.drvMedicalForm.sectionZero.surgery,
    },
    {
      id: REHAB_TRIGGERING_EVENT.ACUTE_EVENT,
      label: translations.patientForms.drvMedicalForm.sectionZero.acuteEvent,
    },
    {
      id: REHAB_TRIGGERING_EVENT.RADIATION,
      label: translations.patientForms.drvMedicalForm.sectionZero.radiation,
    },
    {
      id: REHAB_TRIGGERING_EVENT.ANTINEOPLASTIC_THERAPY,
      label:
        translations.patientForms.drvMedicalForm.sectionZero
          .antineoplasticTherapy,
    },
    {
      id: REHAB_TRIGGERING_EVENT.NOT_APPLICABLE,
      label: translations.patientForms.drvMedicalForm.sectionZero.notApplicable,
    },
    {
      id: REHAB_TRIGGERING_EVENT.ACCIDENT_WORK,
      label: translations.patientForms.drvMedicalForm.sectionZero.accidentWork,
    },
    {
      id: REHAB_TRIGGERING_EVENT.THIRD_PARTY_DAMAGE,
      label:
        translations.patientForms.drvMedicalForm.sectionZero.thirdPartyDamage,
    },
  ];

  return (
    <Section>
      <SectionRow>
        <TextInputWithLabel
          characterLimit={PENSION_INSURANCE_NUMBER_CHARACTER_LIMIT}
          elementName="statutory_pension_insurance_number"
          isBold
          isLarge
          label={
            translations.patientForms.drvMedicalForm.sectionZero.insuranceNumber
          }
        />
      </SectionRow>
      <PatientFormSubheading style={{ margin: margin(0, 0, -1) }}>
        {
          translations.patientForms.drvMedicalForm.sectionZero
            .subtitleHospitalAddress
        }
      </PatientFormSubheading>
      <SectionRow>
        <TextInputWithLabel
          elementName="hospital_name_address"
          isBold
          isLarge
          label={translations.patientForms.drvMedicalForm.addressHospital}
          labelWrapperSx={{ width: "100%" }}
        />
      </SectionRow>
      <PatientFormSubheading style={{ margin: margin(0, 0, -1) }}>
        {
          translations.patientForms.drvMedicalForm.sectionZero
            .subtitleContactPerson
        }
      </PatientFormSubheading>
      <SectionRow>
        <TextInputWithLabel
          elementName="social_worker_name_number"
          isBold
          isLarge
          label={
            translations.patientForms.drvMedicalForm.sectionZero
              .contactPersonInformation
          }
          labelWrapperSx={{ width: "100%" }}
        />
      </SectionRow>
      <PatientFormSubheading style={{ margin: margin(0, 0, -1) }}>
        {translations.patientForms.drvMedicalForm.sectionZero.subtitleAhb}
      </PatientFormSubheading>
      <SectionRow>
        <TextInputWithLabel
          elementName="preferred_rehab_clinic"
          isBold
          isLarge
          label={
            translations.patientForms.drvMedicalForm.sectionZero.addressAhb
          }
          labelWrapperSx={{ width: "100%" }}
        />
      </SectionRow>
      <PatientFormSubheading withBottomPadding>
        {translations.patientForms.drvMedicalForm.sectionZero.typeOfProcedure}
      </PatientFormSubheading>
      <CheckboxInputField
        elementName="pension_procedure_direct"
        label={
          translations.patientForms.drvMedicalForm.sectionZero.directDischarge
        }
      />
      <CheckboxInputField
        elementName="pension_procedure_after_examination"
        label={
          translations.patientForms.drvMedicalForm.sectionZero
            .instructionAfterExamination
        }
      />
      <PatientFormSubheading withBottomPadding>
        {
          translations.patientForms.drvMedicalForm.sectionZero
            .subtitleSuggestedRehab
        }
      </PatientFormSubheading>
      <CheckboxInputField
        elementName="full_reha"
        label={translations.patientForms.drvMedicalForm.sectionZero.static}
      />
      <CheckboxInputField
        elementName="day_reha"
        label={translations.patientForms.drvMedicalForm.sectionZero.mobile}
      />
      <PatientFormSubheading style={{ margin: margin(0, 0, -1) }}>
        {
          translations.patientForms.drvMedicalForm.sectionZero
            .subtitlePatientInformation
        }
      </PatientFormSubheading>
      <SectionRow>
        <TextInputWithLabel
          elementName="patient_name"
          isBold
          isLarge
          label={
            translations.patientForms.drvMedicalForm.sectionZero.patientName
          }
        />
        <DatePickerWithLabel
          elementName="patient_birthdate"
          inputSx={{
            minWidth: SMALL_INPUT_MIN_WIDTH,
            width: "100%",
          }}
          isBold
          label={
            translations.patientForms.drvMedicalForm.sectionZero
              .dateBirthPatient
          }
          wrapperSx={{ width: LARGE_INPUT_WIDTH }}
        />
      </SectionRow>
      <SectionRow>
        <TextInputWithLabel
          elementName="patient_street_housenumber"
          isBold
          isLarge
          label={
            translations.patientForms.drvMedicalForm.sectionZero.addressPatient
          }
        />
      </SectionRow>
      <SectionRow>
        <TextInputWithLabel
          elementName="patient_zipcode"
          isBold
          label={
            translations.patientForms.drvMedicalForm.sectionZero.zipCodePatient
          }
        />
        <TextInputWithLabel
          elementName="patient_city"
          isBold
          isLarge
          label={translations.patientForms.drvMedicalForm.sectionZero.city}
        />
      </SectionRow>
      <PatientFormSubheading style={{ margin: margin(0, 0, -1) }}>
        {translations.patientForms.drvMedicalForm.sectionZero.subtitleHospital}
      </PatientFormSubheading>
      <SectionRow>
        <DatePickerWithLabel
          elementName="admission_date"
          inputSx={{
            minWidth: SMALL_INPUT_MIN_WIDTH,
            width: "100%",
          }}
          isBold
          label={
            translations.patientForms.drvMedicalForm.sectionZero.intakeDate
          }
          wrapperSx={{ width: LARGE_INPUT_WIDTH }}
        />
      </SectionRow>
      <SectionRow>
        <DatePickerWithLabel
          elementName="rehab_triggering_event_date"
          inputSx={{
            minWidth: SMALL_INPUT_MIN_WIDTH,
            width: "100%",
          }}
          isBold
          label={
            translations.patientForms.drvMedicalForm.sectionZero.ahbEventDate
          }
          wrapperSx={{ width: LARGE_INPUT_WIDTH }}
        />
      </SectionRow>
      <SectionRow>
        <ConnectedCheckboxGroup
          elementName="rehab_triggering_event"
          formGroupStyle={{ gap: sizing(1) }}
          items={options}
          label={
            translations.patientForms.drvMedicalForm.sectionZero
              .ahbEventCheckboxGroupLabel
          }
          labelStyle={visuallyHidden}
        />
      </SectionRow>
      <SectionRow>
        <DatePickerWithLabel
          elementName="discharge_date"
          inputSx={{
            minWidth: SMALL_INPUT_MIN_WIDTH,
            width: "100%",
          }}
          isBold
          label={
            translations.patientForms.drvMedicalForm.sectionZero.dischargeDate
          }
          wrapperSx={{ width: LARGE_INPUT_WIDTH }}
        />
        <DatePickerWithLabel
          elementName="transfer_date"
          inputSx={{
            minWidth: SMALL_INPUT_MIN_WIDTH,
            width: "100%",
          }}
          isBold
          label={
            translations.patientForms.drvMedicalForm.sectionZero.ahbStartDate
          }
          wrapperSx={{ width: LARGE_INPUT_WIDTH }}
        />
      </SectionRow>
    </Section>
  );
};
